import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';

export default function BinaYanginAlarmSistemi() {
  useEffect(() => {
    document.title = 'Bina Yangın Alarm Sistemi | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Binalar için profesyonel yangın alarm sistemi kurulumu. TSE standartlarına uygun, 7/24 izleme ve hızlı müdahale imkanı.');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/bina-yangin-alarm-sistemi`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'Bina Yangın Alarm Sistemi | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = 'Binalar için profesyonel yangın alarm sistemi kurulumu. TSE standartlarına uygun, 7/24 izleme ve hızlı müdahale imkanı.';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/bina-yangin-alarm-sistemi`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // Service JSON-LD
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Bina Yangın Alarm Sistemi",
      "description": "Binalar için profesyonel yangın alarm sistemi kurulumu. TSE standartlarına uygun, 7/24 izleme ve hızlı müdahale imkanı.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Fiber Ek",
        "telephone": "+905537165561",
        "url": import.meta.env.VITE_SITE_URL
      },
      "areaServed": {
        "@type": "Place",
        "name": "İstanbul, Kocaeli, Sakarya, Bursa, Tekirdağ, Yalova"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/bina-yangin-alarm-sistemi`
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Hizmetlerimiz', href: '/#hizmetlerimiz' },
    { label: 'Bina Yangın Alarm Sistemi' }
  ];

  const faqData = [
    {
      question: "Yangın alarm sistemi kurulumu zorunlu mu?",
      answer: "Evet, 5 katlı ve üzeri binalar için yasal zorunluluktur. Kadıköy'deki ofis binalarında mutlaka bulunmalıdır."
    },
    {
      question: "Duman dedektörleri nasıl çalışır?",
      answer: "Havadaki duman parçacıklarını algılayarak erken uyarı verir. Optik ve iyonik olmak üzere iki türü vardır."
    },
    {
      question: "Sprinkler sistemi ne zaman devreye girer?",
      answer: "Belirli sıcaklık seviyesine (genellikle 68°C) ulaştığında otomatik olarak su püskürtmeye başlar."
    },
    {
      question: "Yangın alarm sisteminin bakımı ne sıklıkla yapılır?",
      answer: "Ayda bir kontrol, yılda iki kez profesyonel bakım yapılması önerilir. Yasal zorunluluk 6 ayda bir kontroldür."
    },
    {
      question: "Acil çıkış aydınlatması neden önemlidir?",
      answer: "Yangın sırasında elektrik kesintisinde bile çalışarak güvenli tahliye rotasını gösterir."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/e847c6d8faa2f42eecf1ed959f636f44.jpg"
              alt="Kadıköy Ofis Binaları"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-800/70 to-slate-900/80"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-4xl mx-auto text-center text-white pt-8">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Bina Yangın Alarm Sistemi
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Kadıköy Ofis Binaları İçin Profesyonel Yangın Güvenlik Sistemleri
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+905537165561" 
                  className="px-8 py-4 bg-red-500 text-white font-bold rounded-full hover:bg-red-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +90 553 716 55 61
                </a>
                <a 
                  href="https://wa.me/905537165561" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Proje Detayları */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Kadıköy Yangın Alarm Sistemi Projesi
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Kadıköy bölgesindeki ofis binaları ve ticari yapılar için özel olarak tasarlanmış yangın alarm sistemi kurulum projemiz. Modern binaların yangın güvenliği ihtiyaçlarını en üst düzeyde karşılıyoruz.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>Yangın alarm sistemleri</strong> ile erken uyarı</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>Duman dedektörleri</strong> ve ısı sensörleri</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>Sprinkler sistemleri</strong> ile otomatik söndürme</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>Acil çıkış aydınlatması</strong> ile güvenli tahliye</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-red-500 mr-3 text-xl"></i>
                    <span><strong>7/24 sistem bakımı</strong> ve kontrol</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=fire%20alarm%20system%20control%20panel%20in%20Kadikoy%20office%20building%2C%20smoke%20detectors%20installation%2C%20professional%20fire%20safety%20equipment%2C%20red%20emergency%20indicators%2C%20modern%20building%20interior&width=800&height=600&seq=kadikoy-fire-alarm&orientation=landscape"
                  alt="Kadıköy Yangın Alarm Sistemi"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Yangın Güvenlik Sistemleri */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Yangın Güvenlik Sistemlerimiz
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-fire-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Yangın Alarm Paneli</h3>
                <p className="text-gray-600">
                  Merkezi kontrol paneli ile tüm yangın güvenlik sistemlerini tek noktadan yönetin. Kadıköy binalarına özel çözümler.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-cloud-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Duman Dedektörleri</h3>
                <p className="text-gray-600">
                  Optik ve iyonik duman dedektörleri ile erken yangın algılama. Ofis ortamları için hassas algılama sistemi.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-drop-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Sprinkler Sistemleri</h3>
                <p className="text-gray-600">
                  Otomatik sprinkler sistemleri ile yangın söndürme. Islak, kuru ve deluge sistem alternatifleri.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-flashlight-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Acil Çıkış Aydınlatması</h3>
                <p className="text-gray-600">
                  LED acil çıkış aydınlatması ve yönlendirme işaretleri. Güvenli tahliye rotası için kritik sistem.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-volume-up-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Sesli Uyarı Sistemleri</h3>
                <p className="text-gray-600">
                  Yüksek desibel sirenleri ve sesli anons sistemi. Binada bulunan herkesi anında uyarır.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-hand-heart-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Manuel Alarm Butonları</h3>
                <p className="text-gray-600">
                  Kırılabilir camlı manuel alarm butonları. Yangın görüldüğünde anında sistemi aktive eder.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Kadıköy Hakkında */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Kadikoy%20business%20center%20with%20modern%20office%20towers%2C%20commercial%20district%2C%20shopping%20areas%2C%20busy%20urban%20center%2C%20Istanbul%20Asian%20side%20business%20hub&width=800&height=600&seq=kadikoy-business-center&orientation=landscape"
                  alt="Kadıköy İş Merkezi"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Kadıköy Yangın Güvenliği
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Kadıköy, İstanbul'un en yoğun ticari merkezlerinden biridir. Bu bölgedeki yüksek binalar ve ofis komplekslerinin yangın güvenliği ihtiyaçlarını profesyonelce karşılıyoruz.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-building-2-line text-red-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Yüksek Binalar</h4>
                      <p className="text-gray-600">Çok katlı ofis binaları için özel yangın güvenlik sistemi</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-user-3-line text-red-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Yoğun Nüfus</h4>
                      <p className="text-gray-600">Günlük binlerce kişinin çalıştığı iş merkezleri</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-shield-check-line text-red-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Yasal Zorunluluk</h4>
                      <p className="text-gray-600">TSE ve itfaiye onaylı sistemler</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Yangın Güvenlik Standartları */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Yangın Güvenlik Standartları
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">TSE Onaylı</h3>
                <p className="text-gray-600 text-sm">Türk Standartları Enstitüsü onaylı ürünler</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-government-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">İtfaiye Onayı</h3>
                <p className="text-gray-600 text-sm">İtfaiye müdürlüğü tarafından onaylanmış</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">CE Belgeli</h3>
                <p className="text-gray-600 text-sm">Avrupa standartlarına uygun ürünler</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-service-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">7/24 Servis</h3>
                <p className="text-gray-600 text-sm">Kesintisiz bakım ve destek hizmeti</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sıkça Sorulan Sorular
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-red-600 to-orange-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Yangın Alarm Sistemi İçin İletişime Geçin
            </h2>
            <p className="text-red-100 text-xl mb-8 max-w-2xl mx-auto">
              Kadıköy'deki binanız için profesyonel yangın güvenlik sistemleri. Hayat kurtaran teknoloji.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+905537165561" 
                className="px-8 py-4 bg-white text-red-600 font-bold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                +90 553 716 55 61
              </a>
              <a 
                href="https://wa.me/905537165561" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp İletişim
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}